import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api, { API_BASE } from '../../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, AreaChart, Area } from 'recharts';

interface Lead {
    _id: string;
    name: string;
    status: string;
    createdAt: string;
}

export default function BrokerDashboard() {
    const { user, token } = useAuth();
    const { theme } = useTheme();
    const [stats, setStats] = useState({
        totalListings: 0,
        newLeads: 0,
        featuredActive: 0,
        closedDeals: 0
    });
    const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
    const [chartData, setChartData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState<any>(null);
    const [classifiedStats, setClassifiedStats] = useState({
        forSale: 0, forRent: 0, buyReq: 0, rentReq: 0,
        forSaleToday: 0, forRentToday: 0, buyReqToday: 0, rentReqToday: 0
    });
    const [flowData, setFlowData] = useState<any[]>([]);

    const [classifiedSub, setClassifiedSub] = useState<any>(null);

    const fetchData = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const props = await api.getMyProperties(token);
            const propertyList = Array.isArray(props) ? props : [];
            
            const res = await fetch(`${API_BASE}/leads`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const leadList = await res.json();
            const leads = Array.isArray(leadList) ? leadList : [];

            setStats({
                totalListings: propertyList.length,
                newLeads: leads.filter((l: any) => l.status === 'New Inquiry').length,
                featuredActive: propertyList.filter((p: any) => p.isFeatured).length,
                closedDeals: leads.filter((l: any) => l.status === 'Booked').length
            });

            setRecentLeads(leads.slice(0, 5));

            const statusGroups = leads.reduce((acc: any, lead: any) => {
                acc[lead.status] = (acc[lead.status] || 0) + 1;
                return acc;
            }, {});

            setChartData(Object.keys(statusGroups).map(key => ({ name: key, count: statusGroups[key] })));

            const profileData = await api.getUserProfile(token);
            setUserProfile(profileData);

            // Fetch Classified Subscriptions
            const subData = await api.getSubscriptions(token);
            const activeSub = Array.isArray(subData) ? subData.find((s: any) => s.status === 'approved') : null;
            setClassifiedSub(activeSub);

            const cpRes = await fetch(`${API_BASE}/classified-properties`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (cpRes.ok) {
                const cpData = await cpRes.json();
                const cLeads = Array.isArray(cpData) ? cpData : [];

                const cStats = {
                    forSale: 0, forRent: 0, buyReq: 0, rentReq: 0,
                    forSaleToday: 0, forRentToday: 0, buyReqToday: 0, rentReqToday: 0
                };
                const todayStart = new Date();
                todayStart.setHours(0,0,0,0);

                cLeads.forEach(p => {
                    const type = (p.propertyType || '').toLowerCase();
                    const isReq = type.includes('requirement');
                    const isRent = type.includes('rent');
                    const isToday = new Date(p.createdAt) >= todayStart;

                    if (isReq) {
                        if (isRent) { cStats.rentReq++; if (isToday) cStats.rentReqToday++; }
                        else { cStats.buyReq++; if (isToday) cStats.buyReqToday++; }
                    } else {
                        if (isRent) { cStats.forRent++; if (isToday) cStats.forRentToday++; }
                        else { cStats.forSale++; if (isToday) cStats.forSaleToday++; }
                    }
                });
                setClassifiedStats(cStats);

                const days = [...Array(7)].map((_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() - (6 - i));
                    return {
                        name: d.toLocaleDateString('en-US', { weekday: 'short' }),
                        timestamp: new Date(d.setHours(0, 0, 0, 0)).getTime(),
                        count: 0
                    };
                });

                cLeads.forEach(p => {
                    const pTime = new Date(p.createdAt).setHours(0, 0, 0, 0);
                    const day = days.find(d => d.timestamp === pTime);
                    if (day) day.count++;
                });
                setFlowData(days);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [token]);

    const COLORS = ['#7d5fff', '#d9a129', '#a29bfe', '#f3c358', '#4834d4'];

    const isActive = userProfile?.assignedPackage && (!userProfile.packageExpiry || new Date(userProfile.packageExpiry) > new Date());
    const daysLeft = userProfile?.packageExpiry ? Math.ceil((new Date(userProfile.packageExpiry).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : Infinity;

    const classifiedDaysLeft = classifiedSub?.expiryDate ? Math.ceil((new Date(classifiedSub.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

    return (
        <UserDashboardLayout>
            {/* Ultra Modern Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16 px-4">
                <div>
                    <h2 className="text-4xl md:text-7xl font-bold text-[var(--text-primary)] mb-4 leading-tight">
                        Welcome, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--purple)] to-[var(--gold)]">{user?.name?.split(' ')[0]}</span>
                    </h2>
                    <div className="flex items-center gap-4 text-[var(--text-secondary)] font-medium text-sm opacity-60">
                        <i className="fa-solid fa-bolt-lightning text-[var(--gold)]"></i> System Status: Operational <span className="h-1 w-1 bg-[var(--text-secondary)] rounded-full"></span> Real-time Sync Active
                    </div>
                </div>
                <div className="flex flex-wrap gap-4">
                    <Link to="/broker-packages" className="btn-purple no-underline group">
                        Elite Upgrade <i className="fa-solid fa-sparkles ml-2 text-[var(--gold)]"></i>
                    </Link>
                    <Link to="/broker-add-project" className="btn-white no-underline border-[var(--purple)]/20 hover:border-[var(--purple)] group">
                        Add New Project <i className="fa-solid fa-plus ml-2 text-[var(--purple)]"></i>
                    </Link>
                </div>
            </div>

            {/* Subscription Modules Container */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-16 px-4">
                {/* Standard Property Package */}
                {isActive && (
                    <div className="glass-card p-6 md:p-10 relative overflow-hidden group border-[var(--purple)]/20 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.3)] h-full">
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[var(--purple)]/10 rounded-full blur-[80px]"></div>
                        <div className="flex flex-col sm:flex-row items-center gap-6 relative z-10">
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[var(--purple)] to-[var(--gold)] rounded-2xl flex items-center justify-center text-2xl md:text-3xl text-white shadow-xl">
                                <i className="fa-solid fa-crown"></i>
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <p className="text-[10px] font-black text-[var(--purple)] uppercase tracking-widest mb-1">Active Listing Package</p>
                                <h3 className="text-2xl md:text-3xl font-black text-[var(--text-primary)] m-0 truncate">{userProfile.assignedPackage.name}</h3>
                                <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-tighter opacity-50">Remaining</span>
                                        <span className="text-sm font-black text-[var(--text-primary)]">{daysLeft} Days</span>
                                    </div>
                                    <div className="w-[1px] h-6 bg-[var(--purple)]/10 hidden sm:block"></div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-tighter opacity-50">Expires On</span>
                                        <span className="text-sm font-black text-[var(--text-primary)]">{userProfile.packageExpiry ? new Date(userProfile.packageExpiry).toLocaleDateString() : 'Lifetime'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Classified Lead Plan */}
                {classifiedSub ? (
                    <div className="glass-card p-6 md:p-10 relative overflow-hidden group border-emerald-500/20 shadow-[0_40px_80px_-20px_rgba(16,185,129,0.15)] h-full">
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px]"></div>
                        <div className="flex flex-col sm:flex-row items-center gap-6 relative z-10">
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-2xl flex items-center justify-center text-2xl md:text-3xl text-white shadow-xl">
                                <i className="fa-solid fa-bolt"></i>
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">Classified Lead Plan</p>
                                <h3 className="text-2xl md:text-3xl font-black text-[var(--text-primary)] m-0 truncate">{classifiedSub.name}</h3>
                                <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-tighter opacity-50">Link Active</span>
                                        <span className="text-sm font-black text-[var(--text-primary)]">{classifiedDaysLeft} Days Left</span>
                                    </div>
                                    <div className="w-[1px] h-6 bg-emerald-500/10 hidden sm:block"></div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-tighter opacity-50">Termination</span>
                                        <span className="text-sm font-black text-[var(--text-primary)]">{new Date(classifiedSub.expiryDate).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {classifiedDaysLeft <= 3 && (
                            <div className="absolute top-4 right-4">
                                <span className="bg-rose-500 text-white text-[8px] font-black px-2 py-1 rounded-full uppercase tracking-widest">Critical Status</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <Link to="/broker-buy-lead-plan" className="glass-card p-6 md:p-10 flex flex-col items-center justify-center border-dashed border-[var(--purple)]/20 hover:border-emerald-500/50 group h-full no-underline">
                        <div className="w-14 h-14 rounded-full bg-[var(--purple)]/5 flex items-center justify-center text-[var(--purple)] mb-4">
                            <i className="fa-solid fa-plus text-xl"></i>
                        </div>
                        <p className="text-sm font-black text-[var(--text-primary)] uppercase tracking-widest m-0 group-hover:text-emerald-500 transition-colors">Initialize Lead Plan</p>
                        <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-tighter mt-1 opacity-50">Unlock premium classified data stream</p>
                    </Link>
                )}
            </div>

            {/* Metrics Ecosystem */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 mb-16 px-2">
                {[
                    { label: 'Active Listings', value: stats.totalListings, icon: 'fa-city', color: 'var(--purple)' },
                    { label: 'Pipeline Leads', value: stats.newLeads, icon: 'fa-users-viewfinder', color: 'var(--gold)' },
                    { label: 'Star Projects', value: stats.featuredActive, icon: 'fa-star', color: 'var(--purple)' },
                    { label: 'Closed Deals', value: stats.closedDeals, icon: 'fa-handshake-simple', color: 'var(--gold)' }
                ].map((m, idx) => (
                    <div key={idx} className="glass-card p-5 md:p-10 flex flex-col justify-between min-h-[160px] md:min-h-[220px] border-[var(--purple)]/5 group relative overflow-hidden shadow-lg shadow-[var(--purple)]/10">
                        <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-[var(--purple)]/5 rounded-full blur-2xl"></div>
                        <div className="w-10 h-10 md:w-14 md:h-14 bg-[var(--purple)]/10 rounded-xl md:rounded-2xl flex items-center justify-center text-base md:text-xl border border-[var(--purple)]/10" style={{ color: m.color }}>
                            <i className={`fa-solid ${m.icon}`}></i>
                        </div>
                        <div className="mt-4">
                            <h4 className="text-4xl md:text-6xl font-bold text-[var(--text-primary)] m-0 leading-none">{m.value}</h4>
                            <p className="text-[12px] md:text-sm font-bold text-[var(--purple)] mt-2 md:mt-3 leading-tight opacity-100">{m.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Inventory Stream */}
            <div className="mb-16">
                <div className="flex items-center gap-4 md:gap-6 mb-8 md:mb-10 px-2">
                    <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] m-0 whitespace-nowrap">Classified Ads</h3>
                    <div className="h-[1px] md:h-[2px] flex-1 bg-[var(--purple)]/20 rounded-full"></div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 px-2">
                    {[
                        { label: 'Properties for Sale', count: classifiedStats.forSale, today: classifiedStats.forSaleToday, icon: 'fa-tag', path: 'sale' },
                        { label: 'Properties for Rent', count: classifiedStats.forRent, today: classifiedStats.forRentToday, icon: 'fa-key', path: 'rent' },
                        { label: 'Buy Requirements', count: classifiedStats.buyReq, today: classifiedStats.buyReqToday, icon: 'fa-magnifying-glass', path: 'buy' },
                        { label: 'Rent Requirements', count: classifiedStats.rentReq, today: classifiedStats.rentReqToday, icon: 'fa-clipboard-list', path: 'rent' }
                    ].map((c, idx) => (
                        <Link key={idx} to={`/broker-classified-ads?search=${c.path}`} className="glass-card p-5 md:p-8 no-underline group relative overflow-hidden shadow-lg shadow-[var(--purple)]/10">
                            <div className="absolute inset-0 bg-gradient-to-br from-[var(--purple)]/5 to-transparent opacity-0 transition-opacity"></div>
                            <div className="flex items-center justify-between mb-4 md:mb-6 relative z-10">
                                <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-[var(--purple)] to-[var(--gold)] rounded-lg md:rounded-2xl flex items-center justify-center text-white shadow-xl border border-white/20">
                                    <i className={`fa-solid ${c.icon} text-xs md:text-base`}></i>
                                </div>
                                <span className="text-[8px] md:text-[10px] font-bold text-[var(--purple)] bg-[var(--purple)]/10 px-2 md:px-3 py-1 rounded-full">+{c.today}</span>
                            </div>
                            <div className="mt-4">
                                <h4 className="text-4xl md:text-6xl font-bold text-[var(--text-primary)] mb-1 md:mb-2 relative z-10">{c.count}</h4>
                                <p className="text-[12px] md:text-sm font-bold text-[var(--purple)] m-0 relative z-10 leading-tight opacity-100">{c.label}</p>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Property Activity Analytics */}
                <div className="glass-card p-6 md:p-10 mt-10 border-[var(--purple)]/10 shadow-2xl shadow-[var(--purple)]/10 relative overflow-hidden group mx-2">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--purple)]/5 rounded-full blur-[100px] -z-10"></div>
                    <h4 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-8 md:mb-10 px-2">Property Activity (Last 7 Days)</h4>
                    <div className="h-[250px] md:h-[350px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={flowData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.1)" />
                                <XAxis 
                                    dataKey="name" 
                                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }} 
                                    tickLine={false} 
                                    tick={{fill: 'var(--text-secondary)', fontSize: 12, fontWeight: '500'}} 
                                    dy={15} 
                                />
                                <YAxis 
                                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }} 
                                    tickLine={false} 
                                    tick={{fill: 'var(--text-secondary)', fontSize: 12, fontWeight: '500'}} 
                                    domain={[0, 'auto']}
                                />
                                <Tooltip 
                                    cursor={{fill: 'rgba(0,0,0,0.05)'}}
                                    contentStyle={{
                                        backgroundColor: '#fff', 
                                        borderRadius: '8px', 
                                        border: '1px solid #eee', 
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                                        padding: '12px'
                                    }}
                                    itemStyle={{ color: '#7d5fff', fontWeight: 'bold', fontSize: '14px' }}
                                    labelStyle={{ color: '#333', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}
                                />
                                <Bar 
                                    dataKey="count" 
                                    fill="#7d5fff" 
                                    radius={[6, 6, 0, 0]} 
                                    barSize={45}
                                    isAnimationActive={false}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Analytics & CRM Core */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-10">
                    {/* Fresh Activity Stream */}
                    <div className="glass-card p-10 border-[var(--purple)]/10">
                        <div className="flex justify-between items-center mb-10">
                            <h4 className="text-3xl font-bold text-[var(--text-primary)] m-0">Client Pulse</h4>
                            <Link to="/broker-leads" className="text-xs font-bold text-[var(--purple)] hover:underline transition-all">Pipeline Intelligence &rarr;</Link>
                        </div>
                        <div className="space-y-6">
                            {recentLeads.length > 0 ? recentLeads.map((lead) => (
                                <div key={lead._id} className="p-6 rounded-[2.5rem] bg-[var(--bg-main)]/50 border border-[var(--purple)]/10 flex flex-col sm:flex-row sm:items-center justify-between gap-6 group">
                                    <div className="flex items-center gap-6">
                                        <div className="w-16 h-16 rounded-[1.5rem] bg-gradient-to-br from-[var(--purple)]/20 to-[var(--purple)]/5 flex items-center justify-center text-[var(--purple)] font-black text-2xl shadow-inner border border-[var(--purple)]/10">
                                            {lead.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold text-[var(--text-primary)] m-0">{lead.name}</p>
                                            <p className="text-xs font-medium text-[var(--text-secondary)] mt-1 opacity-60">
                                                Captured: {new Date(lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between sm:justify-end gap-6">
                                        <span className={`px-5 py-2 rounded-full text-xs font-bold border shadow-lg ${
                                            lead.status === 'New Inquiry' ? 'bg-[var(--purple)]/10 text-[var(--purple)] border-[var(--purple)]/20' :
                                            lead.status === 'Booked' ? 'bg-[var(--purple)] text-white border-[var(--purple)]' :
                                            'bg-slate-50/5 text-slate-400 border-slate-100/10'
                                        }`}>
                                            {lead.status}
                                        </span>
                                        <Link to="/broker-leads" className="w-12 h-12 rounded-2xl bg-[var(--bg-card)] text-[var(--purple)] flex items-center justify-center border border-[var(--purple)]/20 shadow-xl">
                                            <i className="fa-solid fa-arrow-right-long"></i>
                                        </Link>
                                    </div>
                                </div>
                            )) : (
                                <div className="py-20 text-center text-[var(--text-secondary)]">
                                    <i className="fa-solid fa-satellite-dish text-6xl mb-6 opacity-10"></i>
                                    <p className="font-black text-sm uppercase tracking-[0.3em] opacity-30">Monitoring Activity Stream...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Vertical Control Center */}
                <div className="space-y-10">
                    <div className="glass-card p-10 border-[var(--purple)]/10">
                        <h4 className="text-3xl font-bold text-[var(--text-primary)] mb-10">Quick Access</h4>
                        <div className="grid grid-cols-1 gap-5">
                            {[
                                { label: 'System Profile', icon: 'fa-user-shield', path: '/broker-profile' },
                                { label: 'Asset Manager', icon: 'fa-building-columns', path: '/broker-my-projects' },
                                { label: 'Security Core', icon: 'fa-vault', path: '/broker-change-password' }
                            ].map((s, idx) => (
                                <Link key={idx} to={s.path} className="flex items-center justify-between p-6 rounded-[2rem] bg-[var(--purple)]/5 no-underline border border-transparent shadow-sm">
                                    <div className="flex items-center gap-5">
                                        <div className="w-10 h-10 bg-[var(--purple)]/10 rounded-xl flex items-center justify-center text-[var(--purple)]">
                                            <i className={`fa-solid ${s.icon}`}></i>
                                        </div>
                                        <span className="font-bold text-sm text-[var(--text-primary)]">{s.label}</span>
                                    </div>
                                    <i className="fa-solid fa-arrow-right text-[var(--purple)]/30"></i>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Funnel Intelligence */}
                    <div className="glass-card p-10 border-[var(--purple)]/10">
                        <h4 className="text-3xl font-bold text-[var(--text-primary)] mb-10">Funnel Intel</h4>
                        <div className="h-[280px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={75}
                                        outerRadius={95}
                                        paddingAngle={12}
                                        dataKey="count"
                                        isAnimationActive={false}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{backgroundColor: 'var(--bg-card)', borderRadius: '24px', border: '1px solid var(--border-color)', color: 'var(--text-primary)'}} />
                                    <Legend formatter={(value) => <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em]">{value}</span>} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </UserDashboardLayout>
    );
}
